import axios from 'axios';
import appConfig from '../client_config.js';

const API_URL = appConfig.backend.apiUrl;

// 创建axios实例
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: appConfig.request.timeout,
  withCredentials: appConfig.request.withCredentials, // 跨域请求时携带凭证（cookies）
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 从localStorage获取令牌
    const token = localStorage.getItem(appConfig.auth.tokenKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    console.log(`API请求成功: ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    return response.data;
  },
  (error) => {
    // 处理错误
    if (error.response) {
      // 服务器返回了错误状态码
      console.error(`API错误 (${error.response.status}): ${error.config.method?.toUpperCase()} ${error.config.url}`, error.response.data);
      
      // 如果是401错误，需要区分是登录失败还是token过期
      if (error.response.status === 401) {
        // 如果是登录接口的401错误，不要重定向，让组件处理错误显示
        if (error.config.url && error.config.url.includes('/auth/login')) {
          console.log('登录接口返回401，用户名或密码错误');
        } else {
          // 其他接口的401错误，说明令牌已过期或无效，清除本地存储的令牌并重定向
          console.log('非登录接口返回401，token可能过期，重定向到登录页');
          localStorage.removeItem(appConfig.auth.tokenKey);
          localStorage.removeItem(appConfig.auth.userKey);
          window.location.href = '/login';
        }
      }
    } else if (error.request) {
      // 请求发送了，但没收到响应
      console.error(`网络错误: 无法连接到服务器, URL: ${error.config?.url}`);
    } else {
      // 请求设置时出现问题
      console.error(`请求错误: ${error.message}, URL: ${error.config?.url}`);
    }
    return Promise.reject(error);
  }
);

// 用户认证相关接口
export const authApi = {
  // 用户注册
  register: (userData: { username: string, password: string, inviteCode: string }) => 
    api.post('/auth/register', userData),
  
  // 用户登录
  login: (credentials: { username: string, password: string }) => 
    api.post('/auth/login', credentials),
  
  // 获取当前登录用户信息
  getCurrentUser: () => api.get('/auth/me'),
  
  // 生成邀请码（管理员专用）
  generateInviteCode: () => api.post('/auth/invite-codes'),
  
  // 获取所有邀请码（管理员专用）
  getInviteCodes: () => api.get('/auth/invite-codes'),
  
  // 删除邀请码（管理员专用）
  deleteInviteCode: (id: number) => api.delete(`/auth/invite-codes/${id}`),
  
  // 修改密码
  changePassword: (passwordData: { oldPassword: string, newPassword: string, confirmPassword: string }) => 
    api.put('/auth/change-password', passwordData),
};

// 店铺相关接口
export const shopApi = {
  // 获取所有店铺
  getAllShops: () => api.get('/shops'),
  
  // 根据类型获取店铺
  getShopsByType: (type: string) => api.get(`/shops/type/${type}`),
  
  // 获取单个店铺
  getShop: (id: number) => api.get(`/shops/${id}`),
  
  // 创建店铺
  createShop: (shopData: any) => api.post('/shops', shopData),
  
  // 更新店铺
  updateShop: (id: number, shopData: any) => api.put(`/shops/${id}`, shopData),
  
  // 删除店铺
  deleteShop: (id: number) => api.delete(`/shops/${id}`),
  
  // 更新店铺定时抓取设置
  updateShopSchedule: (id: number, scheduleData: any) => api.put(`/shops/${id}/schedule`, scheduleData),
};

// 数据抓取相关接口
export const dataApi = {
  // 创建数据抓取任务
  createScrapeTask: (shopId: number) => api.post('/data/scrape', { shopId }),
  
  // 一键抓取所有店铺
  createMultipleScrapeTask: (shopType: string, intervalSeconds: number) => 
    api.post('/data/scrape-all', { shopType, intervalSeconds }),
  
  // 获取所有抓取任务
  getAllTasks: () => api.get('/data/tasks'),
  
  // 获取店铺的商品列表
  getProductsByShop: (shopId: number) => api.get(`/data/products/${shopId}`),
  
  // 获取每日分析数据
  getDailyAnalysis: (shopId: number, date?: string) => {
    const params = date ? { date } : {};
    return api.get(`/data/analysis/daily/${shopId}`, { params });
  },
  
  // 获取每周分析数据
  getWeeklyAnalysis: (shopId: number) => api.get(`/data/analysis/weekly/${shopId}`),
  
  // 获取每月分析数据
  getMonthlyAnalysis: (shopId: number) => api.get(`/data/analysis/monthly/${shopId}`),

  // 获取两个日期之间的比较分析数据
  getCompareAnalysis: (shopId: number, endDate: string, startDate?: string) => {
    const params: any = { shopId, endDate };
    if (startDate) {
      params.startDate = startDate;
    }
    return api.get('/data/analysis/compare', { params });
  },
  
  // 获取最后两次采集数据的比较分析
  getLastTwoCrawlsAnalysis: (shopId: number) => {
    console.log(`调用最后两次采集数据分析API: shopId=${shopId}, URL=/data/analysis/last-two-crawls/${shopId}`);
    return api.get(`/data/analysis/last-two-crawls/${shopId}`)
      .then(response => {
        console.log(`最后两次采集数据分析API成功响应:`, response);
        return response;
      })
      .catch(error => {
        console.error('获取最后两次采集数据分析失败:', error);
        if (error.response) {
          console.error('错误响应状态:', error.response.status);
          console.error('错误响应数据:', error.response.data);
        }
        throw error;
      });
  },

  // 获取最后一次采集数据分析
  getLastCrawlAnalysis: (shopId: number) => {
    console.log(`调用最后一次采集数据分析API: shopId=${shopId}, URL=/data/analysis/last-crawl/${shopId}`);
    return api.get(`/data/analysis/last-crawl/${shopId}`)
      .then(response => {
        console.log(`最后一次采集数据分析API成功响应:`, response);
        return response;
      })
      .catch(error => {
        console.error('获取最后一次采集数据分析失败:', error);
        if (error.response) {
          console.error('错误响应状态:', error.response.status);
          console.error('错误响应数据:', error.response.data);
        }
        throw error;
      });
  },

  // 检查特定店铺和日期的数据是否存在
  checkDataExists: (shopId: number, date: string) => {
    return api.get('/data/check-data-exists', { params: { shopId, date } });
  }
};

// 自己店铺相关API
export const getSelfShops = () => api.get('/self-shop/');

export const addSelfShop = (data: { name: string; remarks?: string }) => {
  console.log('调用添加自己店铺API，数据:', data);
  // 确保name字段不为空
  if (!data.name || data.name.trim() === '') {
    return Promise.reject(new Error('店铺名称不能为空'));
  }
  return api.post('/self-shop/', data)
    .then(response => {
      console.log('添加自己店铺API响应:', response);
      return response;
    })
    .catch(error => {
      console.error('添加自己店铺API错误:', error);
      throw error;
    });
};

// 获取自己店铺的抓取任务列表
export const getSelfShopTasks = () => api.get('/self-shop/tasks');

// 创建自己店铺的抓取任务
export const createSelfShopScrapeTask = (shopId: number) => 
  api.post('/self-shop/scrape', { shopId });

// 一键抓取所有自己的店铺
export const createSelfShopScrapeAllTasks = (intervalSeconds: number) => 
  api.post('/self-shop/scrape-all', { intervalSeconds });

// 删除自己店铺的抓取任务
export const deleteSelfShopScrapeTask = (taskId: number) => 
  api.delete(`/self-shop/tasks/${taskId}`);

// 删除对标店铺的抓取任务
export const deleteScrapeTask = (taskId: number) => 
  api.delete(`/data/tasks/${taskId}`);

export const updateSelfShop = (id: number, data: { name: string; remarks?: string }) => {
  console.log(`调用更新自己店铺API，ID: ${id}，数据:`, data);
  // 确保name字段不为空
  if (!data.name || data.name.trim() === '') {
    return Promise.reject(new Error('店铺名称不能为空'));
  }
  return api.put(`/self-shop/${id}`, data)
    .then(response => {
      console.log('更新自己店铺API响应:', response);
      return response;
    })
    .catch(error => {
      console.error('更新自己店铺API错误:', error);
      throw error;
    });
};

export const deleteSelfShop = (id: number) => {
  console.log(`调用删除自己店铺API，ID: ${id}`);
  return api.delete(`/self-shop/${id}`)
    .then(response => {
      console.log('删除自己店铺API响应:', response);
      return response;
    })
    .catch(error => {
      console.error('删除自己店铺API错误:', error);
      throw error;
    });
};

// 自己店铺数据分析API
export const getSelfShopAnalyzeData = (shopId: number, endDate: string, startDate?: string) => {
  const params: any = { shopId, endDate };
  if (startDate) {
    params.startDate = startDate;
  }
  return api.get('/self-shop/analyze', { params });
};

// 获取自己店铺最后两次采集数据分析
export const getSelfShopLastTwoAnalyze = (shopId: number) => {
  return api.get('/self-shop/analyze/last-two', { params: { shopId } });
};

// 获取自己店铺最后一次采集数据分析
export const getSelfShopLastCrawlAnalyze = (shopId: number) => {
  console.log(`调用自己店铺最后一次采集数据分析API: shopId=${shopId}, URL=/self-shop/analyze/last-crawl`);
  return api.get('/self-shop/analyze/last-crawl', { params: { shopId } })
    .then(response => {
      console.log(`自己店铺最后一次采集数据分析API成功响应:`, response);
      return response;
    })
    .catch(error => {
      console.error('获取自己店铺最后一次采集数据分析失败:', error);
      if (error.response) {
        console.error('错误响应状态:', error.response.status);
        console.error('错误响应数据:', error.response.data);
      }
      throw error;
    });
};

// 检查特定自己店铺和日期的数据是否存在
export const checkSelfShopDataExists = (shopId: number, date: string) => {
  return api.get('/self-shop/check-data-exists', { params: { shopId, date } });
};

// 小红书店铺相关API
export const getXhsShops = () => api.get('/xhs-shop/');

export const addXhsShop = (data: { name: string; xhs_hao: string; remarks?: string }) => {
  if (!data.name || data.name.trim() === '') {
    return Promise.reject(new Error('店铺名称不能为空'));
  }
  if (!data.xhs_hao || data.xhs_hao.trim() === '') {
    return Promise.reject(new Error('小红书号不能为空'));
  }
  return api.post('/xhs-shop/', data);
};

export const updateXhsShop = (id: number, data: { name: string; xhs_hao: string; remarks?: string }) => {
  if (!data.name || data.name.trim() === '') {
    return Promise.reject(new Error('店铺名称不能为空'));
  }
  if (!data.xhs_hao || data.xhs_hao.trim() === '') {
    return Promise.reject(new Error('小红书号不能为空'));
  }
  return api.put(`/xhs-shop/${id}`, data);
};

export const deleteXhsShop = (id: number) => api.delete(`/xhs-shop/${id}`);

export const getXhsShopTasks = () => api.get('/xhs-shop/tasks');
export const createXhsShopScrapeTask = (shopId: number) => api.post('/xhs-shop/scrape', { shopId });
export const getXhsShopDataDates = (shopId: number, year: number, month: number): Promise<{ dates: string[] }> =>
  api.get(`/xhs-shop/${shopId}/data-dates?year=${year}&month=${month}`) as Promise<{ dates: string[] }>;
export const createXhsShopScrapeAllTasks = (intervalSeconds: number) => api.post('/xhs-shop/scrape-all', { intervalSeconds });
export const createXhsShopScrapeBatch = (shopIds: number[], intervalSeconds: number) => api.post('/xhs-shop/scrape-batch', { shopIds, intervalSeconds });
export const deleteXhsShopScrapeTask = (taskId: number) => api.delete(`/xhs-shop/tasks/${taskId}`);
export const forceDeleteXhsShopScrapeTask = (taskId: number) => api.delete(`/xhs-shop/tasks/${taskId}/force`);
export const retryXhsShopScrapeTask = (taskId: number) => api.post(`/xhs-shop/tasks/${taskId}/retry`);
export const retryXhsShopScrapeTasksBatch = (taskIds: number[], intervalSeconds?: number) => api.post('/xhs-shop/tasks/retry-batch', { taskIds, intervalSeconds });
export const getXhsProductAliases = (titleHashes: string[]): Promise<Record<string, string>> => api.get('/xhs-shop/product-aliases', { params: { titleHashes: titleHashes.join(',') } });
export const setXhsProductAlias = (titleHash: string, alias: string) => api.post('/xhs-shop/product-alias', { titleHash, alias });
export const getXhsShopQueueStatus = (): Promise<{ websocketConnected: boolean; connectedClients: number; activeTaskCount: number; dbTaskCount: number }> => api.get('/xhs-shop/queue-status');
export const cancelAllXhsShopTasks = () => api.post('/xhs-shop/cancel-all');

export const getXhsShopAnalyzeData = (shopId: number, endDate: string, startDate?: string) => {
  const params: any = { shopId, endDate };
  if (startDate) params.startDate = startDate;
  return api.get('/xhs-shop/analyze', { params });
};

export const getXhsShopLastTwoAnalyze = (shopId: number) => {
  return api.get('/xhs-shop/analyze/last-two', { params: { shopId } });
};

export const getXhsShopLastCrawlAnalyze = (shopId: number) => {
  return api.get('/xhs-shop/analyze/last-crawl', { params: { shopId } });
};

export const checkXhsShopDataExists = (shopId: number, date: string) => {
  return api.get('/xhs-shop/check-data-exists', { params: { shopId, date } });
};

export const getXhsGroupSalesTrend = (groupId: number | string | undefined, days: number): Promise<{ dates: string[]; series: { shopId: number; name: string; data: (number | null)[] }[] }> => {
  const params: any = { days };
  if (groupId !== undefined && groupId !== 'all') {
    params.groupId = groupId;
  }
  return api.get('/xhs-shop/group-sales-trend', { params });
};

export const getXhsShopProductSalesTrend = (shopId: number, days: number): Promise<{ dates: string[]; series: { titleHash: string; name: string; data: (number | null)[] }[] }> => {
  return api.get('/xhs-shop/product-sales-trend', { params: { shopId, days } });
};

// 对标店铺分组相关API
export const shopGroupApi = {
  // 获取所有对标店铺分组
  getAllGroups: () => api.get('/shop-groups'),
  
  // 创建对标店铺分组
  createGroup: (groupData: { group_name: string }) => api.post('/shop-groups', groupData),
  
  // 更新对标店铺分组
  updateGroup: (id: number, groupData: { group_name: string }) => api.put(`/shop-groups/${id}`, groupData),
  
  // 删除对标店铺分组
  deleteGroup: (id: number) => api.delete(`/shop-groups/${id}`),
  
  // 获取分组下的店铺列表
  getShopsByGroup: (groupId: number) => api.get(`/shop-groups/${groupId}/shops`)
};

// 自己店铺分组相关API
export const selfShopGroupApi = {
  // 获取所有自己店铺分组
  getAllGroups: () => api.get('/self-shop-groups'),
  
  // 创建自己店铺分组
  createGroup: (groupData: { group_name: string }) => api.post('/self-shop-groups', groupData),
  
  // 更新自己店铺分组
  updateGroup: (id: number, groupData: { group_name: string }) => api.put(`/self-shop-groups/${id}`, groupData),
  
  // 删除自己店铺分组
  deleteGroup: (id: number) => api.delete(`/self-shop-groups/${id}`),
  
  // 获取分组下的店铺列表
  getShopsByGroup: (groupId: number) => api.get(`/self-shop-groups/${groupId}/shops`)
};

export const xhsShopGroupApi = {
  getAllGroups: () => api.get('/xhs-shop-groups'),
  createGroup: (groupData: { group_name: string }) => api.post('/xhs-shop-groups', groupData),
  updateGroup: (id: number, groupData: { group_name: string }) => api.put(`/xhs-shop-groups/${id}`, groupData),
  deleteGroup: (id: number) => api.delete(`/xhs-shop-groups/${id}`),
  getShopsByGroup: (groupId: number) => api.get(`/xhs-shop-groups/${groupId}/shops`),
  setGroupShops: (groupId: number, shopIds: number[]) => api.put(`/xhs-shop-groups/${groupId}/shops`, { shopIds }),
  addShopToGroup: (groupId: number, shopId: number) => api.post(`/xhs-shop-groups/${groupId}/shops/${shopId}`),
  removeShopFromGroup: (groupId: number, shopId: number) => api.delete(`/xhs-shop-groups/${groupId}/shops/${shopId}`),
  getGroupsByShop: (shopId: number) => api.get(`/xhs-shop-groups/shop/${shopId}/groups`),
};

export default api;
